import { z } from "zod";
import type {
	DealConstructionBase,
	DealConstructionContextBase
} from "~/models/DealConstruction";
import type { ConstructionItem } from "./ConstructionItem";
import type { DealSettlement } from "./DealSettlement";
import type { SettlementItem } from "./SettlementItem";

export type BillingType = "owner" | "rentee";
export type BillingTemplateType = "deal_billing_owner" | "deal_billing_rentee";
export type DealBilling = DealConstructionBase & {
	billingType: BillingType;
	constructionItem: ConstructionItem | null;
	settlementItem: SettlementItem | null;
	taxType: "standard" | "nontaxable";
	transactionType: "billing" | "refund";
	type: "DealBilling";
};

const BILLING_TARGET_NAMES = {
	owner: "オーナー",
	rentee: "入居者"
} as const satisfies Record<BillingType, string>;

export const buildBillingDocumentLabel = (billingType: BillingType): string =>
	`${BILLING_TARGET_NAMES[billingType]}への見積書・請求書`;

export const buildBillingPageTitle = (
	billingType: BillingType,
	settlementEnabled: boolean
): string =>
	`${buildBillingDocumentLabel(billingType)}${settlementEnabled ? "・精算書" : ""}作成`;

// 見積書には有効な工事請求項目のみを出力するため、1件もなければダウンロードさせない
export const canDownloadBillingEstimation = (
	dealConstructions: Pick<DealConstructionBase, "enabled">[]
): boolean => dealConstructions.some((construction) => construction.enabled);

export type DealBillingContext = DealConstructionContextBase & {
	dealSettlements: DealSettlement[];
	settlementOwnerTotalAmount: number;
	settlementOwnerTotalAmountWithTax: number;
	settlementRenteeTotalAmount: number;
	settlementRenteeTotalAmountWithTax: number;
	sumOwnerAmount: number;
	sumOwnerAmountWithTax: number;
	sumRenteeAmount: number;
	sumRenteeAmountWithTax: number;
	type: "DealBillingContext";
};

const MIN_SCALE = 0.1;
const MAX_SCALE = 2;

const MIN_TRUNCATE_SCALE = 0;
const MAX_TRUNCATE_SCALE = 3;

export const billingBulkUpdateSchema = z.object({
	scale: z
		.number({ error: "入力してください" })
		.min(MIN_SCALE, `${MIN_SCALE}以上で入力してください`)
		.max(MAX_SCALE, `${MAX_SCALE}以下で入力してください`),
	truncateScale: z.coerce
		.number()
		.int()
		.min(MIN_TRUNCATE_SCALE)
		.max(MAX_TRUNCATE_SCALE)
});

export type BillingBulkUpdateContext = z.infer<typeof billingBulkUpdateSchema>;
